import { useMemo } from 'react'

import {
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Pagination,
  Paper,
  styled,
} from '@mui/material'

import { TABLE_ROW_CELL_BODY_HEIGHT } from '../../constants'

import { TableCell } from '~/features/guild/components/table/cells'
import { PinnedTable } from '~/features/guild/components/table/pinned'
import { FilterRow, HeaderRow } from '~/features/guild/components/table/rows'
import type { TableProps, TableRow } from '~/features/guild/components/table/types'
import { keyOfRow } from '~/features/guild/components/table/utils'

/** 表格部分的 div wrapper */
const TableWrapper = styled('div')`
  width: 100%;
  flex-direction: row;
  display: flex;
`

export const headerCellSxDefaut = {
  normal: { minWidth: '120px' },
  wide: { minWidth: '150px' },
}

export enum ECellSxSize {
  Normal = 'normal',
  Wide = 'wide',
}

const StyledPaginationContainer = styled('div')`
  margin: 0 auto;
`

export const Table = <T extends TableRow<T, Props>, Props = {}>(props: TableProps<T, Props>) => {
  const hasFilters = useMemo(() => props.columns.some((column) => column.filter), [props.columns])
  const pinLeftColumns = useMemo(() => props.columns.filter((column) => column.pinLeft), [props.columns])
  const pinRightColumns = useMemo(() => props.columns.filter((column) => column.pinRight), [props.columns])

  return (
    <Paper sx={{ width: '100%', mb: 2, display: 'flex', flexDirection: 'column' }}>
      <TableWrapper>
        {pinLeftColumns.length > 0 ? (
          <PinnedTable
            hasFilters={hasFilters}
            componentProps={props}
            dataRows={props.rows}
            pinnedColumns={pinLeftColumns}
            pinnedWidth={props.pinLeftWidth}
          />
        ) : null}
        <MuiTableContainer>
          <MuiTable>
            <MuiTableHead>
              <HeaderRow columns={props.columns} headerHeight={props.headerHeight} tableProps={props.tableProps} />
            </MuiTableHead>
            <MuiTableBody>
              {hasFilters ? <FilterRow componentProps={props} /> : null}
              {props.rows?.map((item, index) => (
                <MuiTableRow key={keyOfRow(item, index, props.tableProps)}>
                  {props.columns.map(
                    (column) =>
                      !column.pinLeft &&
                      !column.pinRight && <TableCell key={column.field.toString()} column={column} row={item} />
                  )}
                </MuiTableRow>
              ))}
              {props.rows?.length === 0 && (
                <MuiTableRow>
                  <MuiTableCell colSpan={props.columns.length}>
                    <div className="table-no-data">无相关数据</div>
                  </MuiTableCell>
                </MuiTableRow>
              )}
            </MuiTableBody>
          </MuiTable>
        </MuiTableContainer>
        {pinRightColumns.length > 0 ? (
          <PinnedTable
            hasFilters={hasFilters}
            componentProps={props}
            dataRows={props.rows}
            pinnedColumns={pinRightColumns}
            pinnedWidth={props.pinRightWidth}
          />
        ) : null}
      </TableWrapper>
      <StyledPaginationContainer>
        <Pagination page={props.page} count={props.count} showFirstButton showLastButton onChange={props.onChange} />
      </StyledPaginationContainer>
    </Paper>
  )
}

export const FormattedTable = <T extends TableRow<T, Props>, Props = {}>(props: TableProps<T, Props>) => {
  const formattedColumns: typeof props.columns = useMemo(
    () =>
      props.columns.map((column) => ({
        ...column,
        bodyCellAlign: 'center',
        headerAlign: 'center',
        bodyHeight: TABLE_ROW_CELL_BODY_HEIGHT,
        headerCellSx:
          typeof column.headerCellSx === 'string'
            ? headerCellSxDefaut[column.headerCellSx]
            : column.headerCellSx || headerCellSxDefaut[ECellSxSize.Normal],
      })),
    [props.columns]
  )
  return (
    <Table
      {...props}
      columns={formattedColumns}
      headerHeight={60}
      filterHeight={90}
      pinLeftWidth={120}
      pinRightWidth={120}
    />
  )
}
